import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, MenuItem, TextField, Tooltip } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config.js";

import { READ_PUBLIC_HOLIDAYS } from "../../graph/holidayManagement/queries.js";
import {
  UPDATE_PUBLIC_HOLIDAY,
  DELETE_PUBLIC_HOLIDAY,
} from "../../graph/holidayManagement/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);

const jurisdictions = [
  "ENGLAND",
  "WALES",
  "SCOTLAND",
  "NORTHERN_IRELAND",
];

const validationSchema = yup.object().shape({
  jurisdiction: yup
    .string()
    .oneOf(jurisdictions)
    .required("Jurisdiction is required"),
  name: yup.string().trim().required("Holiday name is required"),
  date: yup.string().required("Holiday date is required"),
  observedDate: yup.string().nullable(),
  year: yup.number().integer().required("Year is required"),
  source: yup.string().nullable(),
});

const EditPublicHolidayForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const jurisdiction =
    location.state?.jurisdiction || searchParams.get("jurisdiction");
  const year = Number(location.state?.year || searchParams.get("year"));

  const redirectPath =
    location.state?.redirectPath || "/managePublicHolidays";

  useEffect(() => {
    const fetchPublicHoliday = async () => {
      if (!jurisdiction || !year) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Jurisdiction and year are required to load this holiday",
        });
        navigate(redirectPath, { replace: true });
        return;
      }

      try {
        const res = await client.request(READ_PUBLIC_HOLIDAYS, {
          jurisdiction,
          year,
        });

        const holiday = (res?.readPublicHolidays || []).find(
          (item) => item.id === id
        );

        if (!holiday) {
          throw new Error("Public holiday not found");
        }

        setInitialValues({
          jurisdiction: holiday.jurisdiction || jurisdiction,
          name: holiday.name || "",
          date: holiday.date || "",
          observedDate: holiday.observedDate || "",
          year: holiday.year || year,
          source: holiday.source || "",
        });
      } catch (err) {
        console.error("Error fetching public holiday:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load public holiday",
        });
      }
    };

    fetchPublicHoliday();
  }, [id, jurisdiction, year, navigate, redirectPath, setSnackbar]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this public holiday?")) {
      return;
    }

    try {
      const result = await client.request(DELETE_PUBLIC_HOLIDAY, {
        id: String(id),
      });

      if (!result?.deletePublicHoliday) {
        throw new Error("Public holiday was not deleted");
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: "Public holiday deleted successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Delete failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete public holiday",
      });
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      await client.request(UPDATE_PUBLIC_HOLIDAY, {
        input: {
          id,
          jurisdiction: values.jurisdiction,
          name: values.name.trim(),
          date: values.date,
          observedDate: values.observedDate || null,
          year: Number(values.year),
          source: values.source?.trim() || null,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Public holiday updated successfully",
      });

      navigate(redirectPath, {
        state: {
          jurisdiction: values.jurisdiction,
          year: Number(values.year),
        },
      });
    } catch (err) {
      console.error("Update failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update public holiday",
      });
    }
  };

  if (!initialValues) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT PUBLIC HOLIDAY"
          subtitle={`Edit public holiday: ${initialValues.name}`}
        />

        <Tooltip title="Delete public holiday">
          <ActionButton
            tone="danger"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete Public Holiday
          </ActionButton>
        </Tooltip>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                select
                fullWidth
                label="Jurisdiction"
                name="jurisdiction"
                value={values.jurisdiction}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.jurisdiction && !!errors.jurisdiction}
                helperText={touched.jurisdiction && errors.jurisdiction}
                sx={{ gridColumn: "span 2" }}
              >
                {jurisdictions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Holiday Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={values.date}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                error={!!touched.date && !!errors.date}
                helperText={touched.date && errors.date}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                label="Observed Date"
                name="observedDate"
                type="date"
                value={values.observedDate}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={values.year}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.year && !!errors.year}
                helperText={touched.year && errors.year}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                label="Source"
                name="source"
                value={values.source}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditPublicHolidayForm;